// qs-SilhouetteControl.vue
<template>
  <div class="silhouette-control">
    <div
      class="silhouette-main-control"
      @click="toggleDropdown"
      @mouseleave="handleMouseLeave"
    >
      <div
        class="color-preview clickable"
        :style="{ backgroundColor: currentColor }"
        title="Change silhouette color"
        @click.stop="toggleColorPicker"
      />
      <div class="silhouette-label">
        Silhouette
      </div>
      <div
        class="silhouette-preview clickable"
        title="Select silhouette"
        @click.stop="toggleDropdown"
      >
        <img
          v-if="currentSilhouette?.preview"
          :src="currentSilhouette.preview"
          :alt="currentSilhouette.name"
          class="preview-image"
        >
        <div
          v-else
          class="no-silhouette"
        >
          <Image />
        </div>
      </div>
      <button
        class="custom-image-button"
        :class="{
          'has-custom': hasCustomImage,
          'uploading': isUploading,
          'selecting': isFileDialogOpen
        }"
        :title="customImageButtonTitle"
        :disabled="isUploading || isFileDialogOpen"
        @click.stop="handleCustomImageAction"
      >
        <div
          v-if="isUploading || isFileDialogOpen"
          class="upload-spinner"
        >
          <div class="spinner" />
        </div>
        <Upload v-else-if="!hasCustomImage" />
        <X v-else />
      </button>
      <button
        class="visibility-toggle"
        :class="{ active: isVisible }"
        :title="isVisible ? 'Hide silhouette' : 'Show silhouette'"
        @click.stop="handleToggleVisibility"
      >
        <Eye v-if="isVisible" />
        <EyeOff v-else />
      </button>
    </div>
    <div
      v-if="showDropdown"
      class="silhouette-list"
    >
      <div
        v-for="silhouette in allSilhouettes"
        :key="silhouette.id"
        class="silhouette-option"
        :class="{ selected: isSelected(silhouette), custom: silhouette.isCustom }"
        @click="selectSilhouette(silhouette)"
      >
        <div class="option-preview">
          <img
            :src="silhouette.preview"
            :alt="silhouette.name"
            class="option-image"
          >
        </div>
        <div class="option-name">
          {{ silhouette.name }}
        </div>
        <div
          v-if="silhouette.isCustom"
          class="custom-indicator"
        >
          <Upload />
        </div>
        <div
          v-if="isSelected(silhouette)"
          class="selection-indicator"
        >
          <Check />
        </div>
      </div>
    </div>
    <div
      v-if="uploadError"
      class="upload-error"
    >
      <div class="error-message">
        {{ uploadError }}
      </div>
      <button
        class="error-dismiss"
        @click="clearUploadError"
      >
        <X />
      </button>
    </div>
    <div
      v-if="showColorPicker"
      class="color-picker-section"
    >
      <ColorPicker
        :model-value="currentColor"
        :show-preview="false"
        :show-apply-button="true"
        @update:model-value="handleColorChanged"
        @apply="handleColorApply"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { Check, Eye, EyeOff, Image, Upload, X } from 'lucide-vue-next'
import ColorPicker from '@/components/ui/qs-ColorPicker.vue'
import { useSilhouetteStore } from '@/stores/qs-store-silhouette'
import type { Silhouette, SilhouetteControlEmits } from '@/types/qs-type-silhouette'
import { cursorManager } from '@/utils/qs-cursorManager'

interface Props {
  currentSilhouette?: Silhouette | null
  isVisible?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  currentSilhouette: null,
  isVisible: true
})

const emit = defineEmits<SilhouetteControlEmits>()

const silhouetteStore = useSilhouetteStore()

const showDropdown = ref(false)
const showColorPicker = ref(false)
const currentColor = ref(silhouetteStore.color || 'rgba(166, 166, 166, 0.3)')
const previewColor = ref<string | null>(null)

const currentSilhouette = computed(() => props.currentSilhouette ?? silhouetteStore.currentSilhouette ?? null)
const isVisible = computed(() => props.isVisible ?? silhouetteStore.isVisible ?? true)
const allSilhouettes = computed(() => silhouetteStore.allSilhouettes ?? [])
const hasCustomImage = computed(() => silhouetteStore.hasCustomImage ?? false)
const isUploading = computed(() => silhouetteStore.customImageUpload?.isUploading ?? false)
const isFileDialogOpen = computed(() => silhouetteStore.customImageUpload?.isFileDialogOpen ?? false)
const uploadError = computed(() => silhouetteStore.customImageUpload?.uploadError ?? null)

const customImageButtonTitle = computed(() => {
  if (isFileDialogOpen.value) return 'Select image file...'
  if (isUploading.value) return 'Processing image...'
  if (hasCustomImage.value) return 'Remove custom image'
  return 'Upload custom image'
})

const isSelected = (silhouette: Silhouette): boolean => {
  return currentSilhouette.value?.id === silhouette.id
}

const closeDropdown = (): void => {
  showDropdown.value = false
}

const toggleDropdown = (): void => {
  showDropdown.value = !showDropdown.value
  if (showDropdown.value && silhouetteStore.availableSilhouettes.length === 0) {
    silhouetteStore.loadSilhouettes()
  }
}

const selectSilhouette = (silhouette: Silhouette): void => {
  silhouetteStore.setCurrentSilhouette(silhouette)
  emit('silhouette-changed', silhouette)
  closeDropdown()
}

const toggleColorPicker = (): void => {
  showColorPicker.value = !showColorPicker.value
}

const handleColorChanged = (newColor: string): void => {
  previewColor.value = newColor
  emit('color-changed', newColor)
  window.dispatchEvent(new CustomEvent('silhouette-color-preview', {
    detail: { color: newColor }
  }))
}

const handleColorApply = (): void => {
  if (previewColor.value) {
    currentColor.value = previewColor.value
    silhouetteStore.setColor(previewColor.value)
  }
  previewColor.value = null
  showColorPicker.value = false
  window.dispatchEvent(new CustomEvent('silhouette-color-preview-clear'))
}

const handleToggleVisibility = (): void => {
  const newVisibility = !isVisible.value
  silhouetteStore.setVisibility(newVisibility)
  emit('visibility-changed', newVisibility)
}

const handleCustomImageAction = async (): Promise<void> => {
  if (hasCustomImage.value) {
    silhouetteStore.removeCustomImage()
  } else {
    await silhouetteStore.uploadCustomImage()
  }
}

const clearUploadError = (): void => {
  silhouetteStore.customImageUpload?.clearError()
}

const handleMouseLeave = (event: MouseEvent): void => {
  const target = event.target as HTMLElement
  if (target?.classList.contains('silhouette-main-control')) {
    target.blur()
  }
}

const handleKeyDown = (event: KeyboardEvent): void => {
  if (cursorManager.isAnyInputActive() || event.key !== 'Escape') {
    return
  }
  if (showDropdown.value) {
    closeDropdown()
  }
  if (showColorPicker.value) {
    previewColor.value = null
    showColorPicker.value = false
    window.dispatchEvent(new CustomEvent('silhouette-color-preview-clear'))
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown)
  if (!silhouetteStore.availableSilhouettes.length) {
    silhouetteStore.loadSilhouettes().catch((error: unknown) => {
      console.error('Failed to load silhouettes on mount:', error)
    })
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
})
</script>

<style scoped>

.silhouette-control {
  background: theme('colors.creme.light');
  border: none;
  border-radius: 0;
  position: relative;
}

.silhouette-main-control {
  display: flex;
  align-items: center;
  gap: 3px; 
  padding: 6px; 
  background: transparent;
  border: none;
  border-radius: 0;
  margin-bottom: 0;
  transition: all 0.2s ease;
  cursor: pointer;
}

.silhouette-main-control:hover {
  background: theme('colors.creme.hoverpanel'); 
}

.color-preview {
  width: theme('spacing.8'); 
  height: theme('spacing.8'); 
  border: none;
  border-radius: theme('borderRadius.DEFAULT');
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.color-preview.clickable {
  cursor: pointer;
}

.color-preview.clickable:hover {
  border: 1px solid theme('colors.creme.hoverbutton'); 
  box-sizing: border-box;
}


.silhouette-label {
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  color: theme('colors.text.DEFAULT'); 
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 5px; 
}

.silhouette-preview {
  width: theme('spacing.8'); 
  height: theme('spacing.8'); 
  border-radius: theme('borderRadius.DEFAULT');
  display: flex;
  align-items: center;
  justify-content: center;
  background: theme('colors.creme.lighter'); 
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.silhouette-preview.clickable:hover {
  background: theme('colors.creme.hoverbutton'); 
  border: 1px solid theme('colors.creme.hoverbutton'); 
  box-sizing: border-box;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.no-silhouette {
  color: theme('colors.text.DEFAULT'); 
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-silhouette svg {
  flex-shrink: 0;
  width: theme('spacing.4'); 
  height: theme('spacing.4'); 
  stroke-width: 1.5;
  stroke: currentColor;
  fill: none;
}

.visibility-toggle {
  width: 28px; 
  height: theme('spacing.8'); 
  border-radius: theme('borderRadius.DEFAULT'); 
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  color: theme('colors.text.lighter'); 
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}


.visibility-toggle:hover {
  background: theme('colors.creme.hoverbutton'); 
  color: theme('colors.text.DEFAULT'); 
}

.visibility-toggle.active {
  background: transparent;
  color: theme('colors.text.lighter'); 
}

.visibility-toggle.active:hover {
  background: theme('colors.creme.hoverbutton'); 
  color: theme('colors.text.DEFAULT'); 
}

.visibility-toggle svg {
  flex-shrink: 0;
  width: 14px !important; 
  height: 14px !important; 
  stroke-width: 1.5;
  stroke: currentColor;
  fill: none;
}

.silhouette-list {
  position: relative;
  top: 0;
  left: 0;
  right: 0;
  background: transparent;
  border: none;
  border-top: none;
  max-height: theme('spacing.48'); 
  overflow-y: auto;
  z-index: 1000;
  margin-top: theme('spacing.1'); 
}

.silhouette-option {
  display: flex;
  align-items: center;
  padding: 6px theme('spacing.2'); 
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 6px; 
  border-bottom: none;
  background: theme('colors.creme.light');
  border-radius: 0;
  margin-bottom: 0;
}

.silhouette-option:hover {
  background: theme('colors.creme.DEFAULT'); 
}

.silhouette-option.selected {
  background: theme('colors.primary.50'); 
}

.option-preview {
  width: theme('spacing.8'); 
  height: theme('spacing.8'); 
  display: flex;
  align-items: center;
  justify-content: center;
  background: theme('colors.creme.lighter'); 
  border: none;
  border-radius: theme('borderRadius.DEFAULT');
  flex-shrink: 0;
}

.option-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.option-name {
  flex: 1;
  font-size: theme('fontSize.sm'); 
  font-weight: theme('fontWeight.medium'); 
  color: theme('colors.text.DEFAULT'); 
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selection-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  color: theme('colors.primary.500'); 
  flex-shrink: 0;
}

.selection-indicator svg {
  flex-shrink: 0;
  width: 14px !important; 
  height: 14px !important; 
  stroke-width: 1.5;
  stroke: currentColor;
  fill: none;
}

.custom-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  color: theme('colors.amber.500'); 
  flex-shrink: 0;
  margin-right: theme('spacing.1'); 
}

.custom-indicator svg {
  flex-shrink: 0;
  width: 14px !important; 
  height: 14px !important; 
  stroke-width: 1.5;
  stroke: currentColor;
  fill: none;
}

.custom-image-button {
  width: 28px; 
  height: theme('spacing.8'); 
  border-radius: theme('borderRadius.DEFAULT'); 
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  color: theme('colors.text.lighter'); 
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.custom-image-button:hover {
  background: theme('colors.creme.hoverbutton'); 
  color: theme('colors.text.DEFAULT'); 
}

.custom-image-button.has-custom {
  background: theme('colors.danger.light'); 
  color: theme('colors.danger.DEFAULT'); 
}

.custom-image-button.has-custom:hover {
  background: theme('colors.danger.light'); 
  color: theme('colors.danger.dark'); 
}

.custom-image-button.uploading,
.custom-image-button.selecting {
  cursor: not-allowed;
  opacity: 0.7;
}

.custom-image-button.selecting {
  background: theme('colors.primary.50'); 
  color: theme('colors.primary.500'); 
}

.custom-image-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.custom-image-button svg {
  flex-shrink: 0;
  width: 14px !important; 
  height: 14px !important; 
  stroke-width: 1.5;
  stroke: currentColor;
  fill: none;
}

.upload-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 14px; 
  height: 14px; 
  border: 2px solid theme('colors.gray.200'); 
  border-top: 2px solid theme('colors.primary.500'); 
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.silhouette-option.custom {
  border-left: 3px solid theme('colors.amber.500'); 
  padding-left: 4px; 
}


.upload-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: theme('spacing.2') theme('spacing.3'); 
  background: theme('colors.danger.light'); 
  border: 1px solid theme('colors.danger.light'); 
  border-radius: theme('borderRadius.DEFAULT');
  margin-top: theme('spacing.1'); 
  gap: 6px; 
}

.error-message {
  flex: 1;
  font-size: theme('fontSize.xs'); 
  color: theme('colors.danger.DEFAULT'); 
  font-weight: theme('fontWeight.medium'); 
}

.error-dismiss {
  width: theme('spacing.5'); 
  height: theme('spacing.5'); 
  border: none;
  background: transparent;
  cursor: pointer;
  color: theme('colors.danger.DEFAULT'); 
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: theme('borderRadius.sm');
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.error-dismiss:hover {
  background: theme('colors.danger.light'); 
}

.color-picker-section {
  position: relative;
  top: 0;
  left: 0;
  right: 0;
  background: transparent;
  border: none;
  z-index: 1000;
  padding: 0;
  margin-top: theme('spacing.1'); 
}
</style>